import {
  Accordion,
  AccordionSet,
  ContentCard,
  IconButton,
  Row,
} from "@narmi/design_system";
import { useNotificationContext } from "cerulean";

import { ReactNode } from "react";
import { useLocalization } from "@fluent/react";


const useCopyText = () => {
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();
  return {
    copyText: async (text: string) => {
      await navigator.clipboard.writeText(text);
      sendNotification({
        type: "dismissible_success",
        text: l10n.getString("notification-link-copied"),
      });
    },
  };
};

const InstructionPhase = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className={"margin--bottom"}>
    <div className={"fontWeight--bold margin--bottom"}>{title}</div>
    <div>{children}</div>
  </div>
);

const getAccordionSummary =
  (title: string, className: string = "") =>
  (isOpen: boolean) => (
    <div
      style={{
        backgroundColor: isOpen
          ? "rgba(var(--theme-rgb-primary), 0.1)"
          : "transparent",
        color: "var(--theme-primary)",
        fontWeight: "var(--font-weight-semibold)",
      }}
      className={`alignChild--left--center padding--all--m ${className}`.trim()}
    >
      <Row alignItems="center" justifyContent="space-between">
        <Row.Item>{title}</Row.Item>
        <Row.Item shrink>
          <IconButton
            kind="themed"
            name={isOpen ? "chevron-up" : "chevron-down"}
          />
        </Row.Item>
      </Row>
    </div>
  );

const PlanInfo = ({
  name,
  plans,
}: {
  name: string;
  plans: string;
}) => (
  <div>
    <strong>{name}:</strong> {plans}
  </div>
);

const renderTextWithLink = (
  text: string,
  linkText: string,
  href: string,
) => {
  const parts = text.split(linkText);
  return (
    <>
      {parts[0]}
      <a href={href} target="_blank" rel="noopener noreferrer">
        {linkText}
      </a>
      {parts[1]}
    </>
  );
};

const SetupInstructionsAccordion = () => {
  const { l10n } = useLocalization();

  return (
    <div className={"border--all rounded--all--m"}>
      <AccordionSet behavior="exclusive">
        <Accordion
          renderSummary={getAccordionSummary(
            l10n.getString("mcp-connectors-setup-claude-title"),
          )}
        >
          <div className="padding--all">
            <InstructionPhase
              title={l10n.getString("mcp-claude-phase-add-connector")}
            >
              <div>
                {renderTextWithLink(
                  l10n.getString("mcp-claude-step-1", {
                    linkText: "Claude.ai",
                  }),
                  "Claude.ai",
                  "https://claude.ai/settings/connectors",
                )}
              </div>
              <div>{l10n.getString("mcp-claude-step-2")}</div>
              <div>{l10n.getString("mcp-claude-step-3")}</div>
              <div>{l10n.getString("mcp-claude-step-4")}</div>
              <div>{l10n.getString("mcp-claude-step-5")}</div>
            </InstructionPhase>

            <InstructionPhase
              title={l10n.getString("mcp-claude-phase-authenticate")}
            >
              <div>{l10n.getString("mcp-claude-step-6")}</div>
              <div>{l10n.getString("mcp-claude-step-7")}</div>
              <div>{l10n.getString("mcp-claude-step-8")}</div>
            </InstructionPhase>

            <InstructionPhase
              title={l10n.getString("mcp-claude-phase-start-using")}
            >
              <div>{l10n.getString("mcp-claude-step-9")}</div>
            </InstructionPhase>
          </div>
        </Accordion>
        <Accordion
          renderSummary={getAccordionSummary(
            l10n.getString("mcp-connectors-setup-chatgpt-title"),
            "border--top",
          )}
        >
          <div className="padding--all">
            <InstructionPhase
              title={l10n.getString("mcp-chatgpt-phase-enable-dev-mode")}
            >
              <div>
                {renderTextWithLink(
                  l10n.getString("mcp-chatgpt-step-1", {
                    linkText: "chatgpt.com",
                  }),
                  "chatgpt.com",
                  "https://chatgpt.com/#settings/Connectors",
                )}
              </div>
              <div>{l10n.getString("mcp-chatgpt-step-2")}</div>
            </InstructionPhase>

            <InstructionPhase
              title={l10n.getString("mcp-chatgpt-phase-create-connector")}
            >
              <div>{l10n.getString("mcp-chatgpt-step-3")}</div>
              <div>{l10n.getString("mcp-chatgpt-step-4")}</div>
              <div>{l10n.getString("mcp-chatgpt-step-5")}</div>
              <div>{l10n.getString("mcp-chatgpt-step-6")}</div>
              <div>{l10n.getString("mcp-chatgpt-step-7")}</div>
            </InstructionPhase>

            <InstructionPhase
              title={l10n.getString("mcp-chatgpt-phase-authenticate")}
            >
              <div>{l10n.getString("mcp-chatgpt-step-8")}</div>
              <div>{l10n.getString("mcp-chatgpt-step-9")}</div>
              <div>{l10n.getString("mcp-chatgpt-step-10")}</div>
            </InstructionPhase>

            <InstructionPhase
              title={l10n.getString("mcp-chatgpt-phase-start-using")}
            >
              <div>{l10n.getString("mcp-chatgpt-step-11")}</div>
            </InstructionPhase>
          </div>
        </Accordion>
      </AccordionSet>
    </div>
  );
};

const TroubleshootingSection = ({
  tokenLifespanDays,
  institutionName,
}: {
  tokenLifespanDays: number;
  institutionName: string;
}) => {
  const { l10n } = useLocalization();
  const connectorName = institutionName
    ? l10n.getString("mcp-connectors-ai-connector-name", { institutionName })
    : l10n.getString("mcp-connectors-ai-connector-name-default");

  return (
    <div>
      <div className="fontWeight--bold margin--bottom">
        {l10n.getString("mcp-connectors-troubleshooting-title")}
      </div>
      <ul
        style={{
          paddingLeft: "20px",
          margin: 0,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <li>
          {l10n.getString("mcp-connectors-reauth-notice", {
            days: tokenLifespanDays,
          })}
          <ul style={{ paddingLeft: "20px", marginTop: "4px" }}>
            <li>{l10n.getString("mcp-connectors-reauth-claude")}</li>
            <li>
              {l10n.getString("mcp-connectors-reauth-chatgpt", {
                connectorName,
              })}
            </li>
          </ul>
        </li>
        <li>{l10n.getString("mcp-connectors-signers-notice")}</li>
        <li>{l10n.getString("mcp-connectors-team-auth-notice")}</li>
      </ul>
    </div>
  );
};

const PersonalLink = ({
  mcpLink,
  onCopy,
}: {
  mcpLink: string;
  onCopy: () => void;
}) => {
  const { l10n } = useLocalization();

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "rgba(var(--theme-rgb-primary), 0.1)",
      }}
      className={"rounded--all--m padding--all--m"}
    >
      <div>{l10n.getString("mcp-connectors-personal-link-label")}</div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          fontWeight: "bold",
        }}
      >
        <div>{mcpLink}</div>
        &nbsp;
        <IconButton kind="themed" name="copy" onClick={onCopy} />
      </div>
    </div>
  );
};

const MCPConnectorsCard = ({
  mcpServerHostname,
  tokenLifespanDays,
  institutionName,
}: {
  mcpServerHostname: string;
  tokenLifespanDays: number;
  institutionName: string;
}) => {
  const { copyText } = useCopyText();
  const { l10n } = useLocalization();
  const mcpLink = `https://${mcpServerHostname}/mcp`;

  return (
    <div
      style={{
        maxWidth: "700px",
        boxShadow: "var(--elevation-low)",
        margin: "75px auto",
      }}
      className={"nds-typography"}
    >
      <ContentCard>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "25px",
          }}
        >
          <div className="fontSize--l fontWeight--bold">
            {l10n.getString("mcp-connectors-title")}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <PlanInfo
              name="Claude"
              plans={l10n.getString("mcp-connectors-claude-plans")}
            />
            <PlanInfo
              name="ChatGPT"
              plans={l10n.getString("mcp-connectors-chatgpt-plans")}
            />
          </div>
          <PersonalLink mcpLink={mcpLink} onCopy={() => copyText(mcpLink)} />
          <SetupInstructionsAccordion />
          <TroubleshootingSection
            tokenLifespanDays={tokenLifespanDays}
            institutionName={institutionName}
          />
        </div>
      </ContentCard>
    </div>
  );
};
export default MCPConnectorsCard;
